/**
 * Zone Overlay Layer Configuration
 *
 * Provides field zone polygon overlays with average moisture coloring
 * using MapLibre GL JS fill and symbol layers.
 *
 * Features:
 * - Field zone polygons from GeoJSON data
 * - Color zones by average moisture of sensors within
 * - Zone labels (name + average moisture %)
 * - Click-to-filter support for zone selection
 * - Configurable fill opacity and stroke width
 * - Hover highlighting
 *
 * @see https://maplibre.org/maplibre-style-spec/layers/#fill
 */

import type {
  FillLayerSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
  ExpressionSpecification,
} from 'maplibre-gl';
import type {
  ZoneLayerConfig,
  ZoneDataPoint,
  ZoneFeature,
  ZoneGeoJSON,
  SensorDataPoint,
} from '../MapComponent.types';

// ============================================================================
// Zone Data Processing
// ============================================================================

/**
 * Convert zone data points to GeoJSON FeatureCollection
 *
 * @param zones - Array of zone data points
 * @returns GeoJSON FeatureCollection for zones
 */
export const convertZonesToGeoJSON = (zones: ZoneDataPoint[]): ZoneGeoJSON => {
  const features: ZoneFeature[] = zones.map(zone => ({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: zone.coordinates,
    },
    properties: {
      id: zone.id,
      name: zone.name,
      averageMoisture: zone.averageMoisture,
      sensorCount: zone.sensorCount,
      color: zone.color,
    },
  }));

  return {
    type: 'FeatureCollection',
    features,
  };
};

/**
 * Calculate average moisture for each zone based on sensor data
 *
 * @param zones - Array of zone data points
 * @param sensors - Array of sensor data points
 * @returns Zones with updated average moisture values
 */
export const calculateZoneMoisture = (
  zones: ZoneDataPoint[],
  sensors: SensorDataPoint[]
): ZoneDataPoint[] => {
  return zones.map(zone => {
    // Find sensors within this zone
    const zoneSensors = sensors.filter(sensor => sensor.zone === zone.id || sensor.zone === zone.name);

    if (zoneSensors.length === 0) {
      return { ...zone, averageMoisture: undefined, sensorCount: 0 };
    }

    const totalMoisture = zoneSensors.reduce((sum, s) => sum + s.moistureLevel, 0);
    const averageMoisture = Math.round((totalMoisture / zoneSensors.length) * 10) / 10;

    return {
      ...zone,
      averageMoisture,
      sensorCount: zoneSensors.length,
    };
  });
};

// ============================================================================
// Color Configuration
// ============================================================================

/**
 * Zone fill color expression based on average moisture
 */
export const zoneFillColorExpression: ExpressionSpecification = [
  'case',
  ['has', 'averageMoisture'],
  [
    'interpolate',
    ['linear'],
    ['get', 'averageMoisture'],
    0, '#B26640',    // brown (dry)
    30, '#DEB464',   // tan
    50, '#41AB5D',   // green (optimal)
    70, '#67A9CF',   // light blue
    100, '#2166AC', // blue (saturated)
  ],
  // Default color when no moisture data
  '#9CA3AF',
];

/**
 * Zone stroke color (darker version for borders)
 */
export const zoneStrokeColorExpression: ExpressionSpecification = [
  'case',
  ['has', 'averageMoisture'],
  [
    'interpolate',
    ['linear'],
    ['get', 'averageMoisture'],
    0, '#8B4513',    // dark brown
    30, '#B8860B',   // dark tan
    50, '#228B22',   // dark green
    70, '#4682B4',   // steel blue
    100, '#1A4D7C', // dark blue
  ],
  '#6B7280',
];

// ============================================================================
// Paint Configuration
// ============================================================================

/**
 * Get zone fill paint configuration
 *
 * @param config - Zone layer configuration
 * @returns MapLibre fill paint specification
 */
export const getZoneFillPaint = (
  config: ZoneLayerConfig
): FillLayerSpecification['paint'] => {
  return {
    'fill-color': zoneFillColorExpression,
    'fill-opacity': [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      Math.min(1, config.fillOpacity + 0.2),
      config.fillOpacity,
    ] as ExpressionSpecification,
  };
};

/**
 * Get zone stroke (outline) paint configuration
 *
 * @param config - Zone layer configuration
 * @returns MapLibre line paint specification
 */
export const getZoneStrokePaint = (
  config: ZoneLayerConfig
): LineLayerSpecification['paint'] => ({
  'line-color': zoneStrokeColorExpression,
  'line-width': [
    'case',
    ['boolean', ['feature-state', 'hover'], false],
    config.strokeWidth + 1,
    config.strokeWidth,
  ] as ExpressionSpecification,
  'line-opacity': 0.8,
});

/**
 * Get zone label paint configuration
 *
 * @returns MapLibre symbol paint specification
 */
export const getZoneLabelPaint = (): SymbolLayerSpecification['paint'] => ({
  'text-color': '#1f2937',
  'text-halo-color': 'rgba(255, 255, 255, 0.9)',
  'text-halo-width': 2,
});

/**
 * Get zone label layout configuration
 *
 * @returns MapLibre symbol layout specification
 */
export const getZoneLabelLayout = (): SymbolLayerSpecification['layout'] => ({
  'text-field': [
    'concat',
    ['get', 'name'],
    '\n',
    [
      'case',
      ['has', 'averageMoisture'],
      ['concat', ['to-string', ['round', ['get', 'averageMoisture']]], '%'],
      'N/A',
    ],
  ] as ExpressionSpecification,
  'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
  'text-size': 12,
  'text-anchor': 'center',
  'text-allow-overlap': false,
  'text-ignore-placement': false,
  'symbol-placement': 'point',
});

// ============================================================================
// Layer IDs
// ============================================================================

export const ZONE_FILL_LAYER_ID = 'zone-fill';
export const ZONE_STROKE_LAYER_ID = 'zone-stroke';
export const ZONE_LABEL_LAYER_ID = 'zone-labels';
export const ZONE_SOURCE_ID = 'zones';

// ============================================================================
// Default Configuration
// ============================================================================

/**
 * Default zone layer configuration
 */
export const defaultZoneConfig: ZoneLayerConfig = {
  enabled: false,
  fillOpacity: 0.3,
  strokeWidth: 2,
};

/**
 * Default zone data - empty by default, requires data source configuration
 */
export const DEFAULT_ZONE_DATA: ZoneDataPoint[] = [];

// ============================================================================
// Layer Specifications
// ============================================================================

/**
 * Get zone fill layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @param config - Zone layer configuration
 * @returns Full fill layer specification
 */
export const getZoneFillLayerSpec = (
  sourceId: string,
  config: ZoneLayerConfig
): Omit<FillLayerSpecification, 'source-layer'> => ({
  id: ZONE_FILL_LAYER_ID,
  type: 'fill',
  source: sourceId,
  paint: getZoneFillPaint(config),
});

/**
 * Get zone stroke layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @param config - Zone layer configuration
 * @returns Full line layer specification
 */
export const getZoneStrokeLayerSpec = (
  sourceId: string,
  config: ZoneLayerConfig
): Omit<LineLayerSpecification, 'source-layer'> => ({
  id: ZONE_STROKE_LAYER_ID,
  type: 'line',
  source: sourceId,
  paint: getZoneStrokePaint(config),
});

/**
 * Get zone label layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @returns Full symbol layer specification
 */
export const getZoneLabelLayerSpec = (
  sourceId: string
): Omit<SymbolLayerSpecification, 'source-layer'> => ({
  id: ZONE_LABEL_LAYER_ID,
  type: 'symbol',
  source: sourceId,
  paint: getZoneLabelPaint(),
  layout: getZoneLabelLayout(),
});

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a point is inside a polygon (ray casting algorithm)
 *
 * @param point - [longitude, latitude] coordinates
 * @param polygon - Polygon coordinates (first ring is outer boundary)
 * @returns True if point is inside polygon
 */
export const isPointInPolygon = (
  point: [number, number],
  polygon: number[][][]
): boolean => {
  const [x, y] = point;
  const ring = polygon[0]; // Use outer ring
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];

    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Find sensors within a zone
 *
 * @param zone - Zone data point with polygon
 * @param sensors - Array of sensor data points
 * @returns Sensors that are geographically within the zone
 */
export const findSensorsInZone = (
  zone: ZoneDataPoint,
  sensors: SensorDataPoint[]
): SensorDataPoint[] => {
  return sensors.filter(sensor =>
    isPointInPolygon([sensor.longitude, sensor.latitude], zone.coordinates)
  );
};

/**
 * Get zone center coordinates for label placement
 *
 * @param coordinates - Zone polygon coordinates
 * @returns [longitude, latitude] center point
 */
export const getZoneCentroid = (coordinates: number[][][]): [number, number] => {
  const ring = coordinates[0];
  let sumX = 0;
  let sumY = 0;

  for (let i = 0; i < ring.length - 1; i++) {
    sumX += ring[i][0];
    sumY += ring[i][1];
  }

  const count = ring.length - 1;
  return [sumX / count, sumY / count];
};

/**
 * Format zone information for display
 *
 * @param zone - Zone feature properties
 * @returns Formatted zone info
 */
export const formatZoneInfo = (properties: ZoneFeature['properties']): {
  name: string;
  moisture: string;
  sensorCount: string;
} => ({
  name: properties.name,
  moisture: properties.averageMoisture !== undefined
    ? `${properties.averageMoisture.toFixed(1)}%`
    : 'N/A',
  sensorCount: properties.sensorCount !== undefined
    ? `${properties.sensorCount} sensors`
    : 'Unknown',
});

export default {
  convertZonesToGeoJSON,
  calculateZoneMoisture,
  getZoneFillPaint,
  getZoneStrokePaint,
  getZoneFillLayerSpec,
  getZoneStrokeLayerSpec,
  getZoneLabelLayerSpec,
  isPointInPolygon,
  findSensorsInZone,
  getZoneCentroid,
  formatZoneInfo,
  defaultZoneConfig,
  DEFAULT_ZONE_DATA,
  ZONE_FILL_LAYER_ID,
  ZONE_STROKE_LAYER_ID,
  ZONE_LABEL_LAYER_ID,
  ZONE_SOURCE_ID,
  zoneFillColorExpression,
  zoneStrokeColorExpression,
};
